import React, { useState, useEffect } from "react";
import Select from "react-select";
import _, { debounce } from "lodash";
import { getProjectDetail, getProjects } from "api/project.api";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import {
  addAllIndustrysegment,
  addCandidateWorkCompanyList,
  addEducationFields,
  addExcludeLocations,
  addFullName,
  addIndustry,
  addIndustryType,
  addIndustryTypeAsync,
  addJobTitle,
  addKeyword,
  addLocation,
  addLocations,
  addMaxExp,
  addMinExp,
  addName,
  addSkills,
  addUniversity,
  changeIsNamedSearch,
  clearAllLocation,
  removeAllJobTitle,
  resetKeyword,
  resetLocations,
  resetSearchConfig,
  setAgencyParent,
  setEverWorked,
  setExactJobTitleMatch,
  setExcludeSubsidiaries,
  setIncludeSubsidiaries,
  setMultipleAgency,
  setMultipleAgencyType,
  setSelectedProject,
  setTitleWithPreviousJob,
  setWorkOnPreviousCompany,
  updateLocationRadius,
} from "store/slices/searcSlice";
import { getSearchLogDetails } from "api/search.api";

interface ProjectDetails {
  id: string;
  title: string; // Title for the project
}

interface ProjectSelectProps {
  onSelect?: (selectedOption: ProjectDetails | null) => void;
  styles?: any; // Optional: Allow custom styles to be passed
  isModal?: boolean; // Optional: If true, display the select in a modal
}

const ProjectSelect: React.FC<ProjectSelectProps> = ({ onSelect, styles, isModal }) => {
  const dispatch = useAppDispatch();
  const defaultSelectedProject = useAppSelector((state) => state.search.selectedProject);
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const activeStep = useAppSelector((state) => state.search.active_step);
  const queryParams = new URLSearchParams(location.search);
  const jobId = queryParams.get("job-id");
  const searchId = queryParams.get("searchId");
  // Fetch projects based on the current page and search term
  const fetchProjects = (currentPage: number, search: string) => {
    if (!hasMore) return;

    setLoading(true);
    // getProjects(currentPage, search)
    getProjects(currentPage)
      .then((result: any) => {
        if (currentPage === 1) {
          setOptions(result.results); // Reset options on new search
        } else {
          setOptions((prevOptions) => [...prevOptions, ...result.results]); // Append options for infinite scroll
        }
        if (defaultSelectedProject?.id && defaultSelectedProject.id < 3 && !isModal && !searchId) {
          dispatch(setSelectedProject(result.results[0]));
        }
        setHasMore(!!result.next); // If there's a next page, keep loading more
        setPage(currentPage + 1); // Increment the page number for the next fetch
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getSingleProject = (id: number) => {
    setLoading(true);
    getProjectDetail(id)
      .then((project) => {
        // setOptions((prevOptions) => [...prevOptions, project]);
        dispatch(setSelectedProject(project));
        if (!isModal && !searchId) {
          //resetKeyword
          dispatch(resetSearchConfig());
          dispatch(resetKeyword());
          if (project.keywords) dispatch(addKeyword(project.keywords));
          dispatch(clearAllLocation());
          if (project.locations && project.locations.length > 0)
            dispatch(addLocation(project.locations));
          dispatch(removeAllJobTitle([]));
          if (project.title) dispatch(addJobTitle(project.official_title));

          dispatch(addName(project.title));
          if (project.min_experience) {
            dispatch(addMinExp(project?.min_experience));
          }
          if (project.max_experience) {
            dispatch(addMaxExp(project?.max_experience));
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching project details:", error);
      });
  };
  // Debounced fetch function to prevent too many API calls
  const debouncedFetchProjects = debounce(fetchProjects, 300);

  // Load initial projects on component mount
  useEffect(() => {
    if (!jobId) {
      fetchProjects(page, searchTerm);
    }
  }, []);

  // Handle input change (search term) in the dropdown
  const handleInputChange = (inputValue: string) => {
    setSearchTerm(inputValue);
    setPage(1); // Reset to the first page for new search
    debouncedFetchProjects(1, inputValue);
  };

  // Handle scrolling in the dropdown to load more options
  const handleMenuScrollToBottom = () => {
    if (!loading && hasMore) {
      debouncedFetchProjects(page, searchTerm);
    }
  };

  const customStyles = {
    container: (provided: any) => ({
      ...provided,
    }),
    control: (provided: any) => ({
      ...provided,
      border: "2px solid #9E77ED",
    }),
    option: (provided: any) => ({
      ...provided,
      color: "#101828",
    }),
  };

  const handleChange = async (searchId: any) => {
    try {
      setLoading(true);
      const res = await getSearchLogDetails(searchId);
      dispatch(resetSearchConfig());
      dispatch(addName(res.search_title));
      dispatch(addJobTitle(res?.search_wizard_config?.job_title));
      // dispatch(addName(res?.search_name));
      if (res.skills) {
        dispatch(addSkills(res.skills));
      }
      dispatch(addFullName(res.full_name ? res.full_name : null));

      dispatch(
        setEverWorked(
          res?.search_wizard_config?.ever_worked ? res?.search_wizard_config?.ever_worked : false
        )
      );

      dispatch(changeIsNamedSearch(res?.full_name ? true : false));
      if (res?.exact_job_title_match) {
        dispatch(setExactJobTitleMatch(res?.exact_job_title_match));
      }
      if (res?.allow_previous_job_titles) {
        dispatch(setTitleWithPreviousJob(res?.allow_previous_job_titles));
      }
      if (res?.include_subsidiaries) {
        dispatch(setIncludeSubsidiaries(res?.include_subsidiaries));
      }
      if (res?.exclude_subsidiaries) {
        dispatch(setExcludeSubsidiaries(res?.exclude_subsidiaries));
      }
      if (res?.worked_previously_at_companies) {
        dispatch(setWorkOnPreviousCompany(res?.worked_previously_at_companies));
      }
      if (res?.keywords) {
        dispatch(addKeyword(res?.keywords));
      }
      if (res?.industries && res?.industries.length > 0) {
        await dispatch(addIndustryTypeAsync(res?.industries)).unwrap();
      }
      if (res?.agency_tree?.selected_groups) {
        res?.agency_tree?.selected_groups.forEach((agency: any) => {
          dispatch(setAgencyParent(agency));
        });
      }
      if (res.search_wizard_config?.agencyType) {
        dispatch(setMultipleAgencyType(res?.search_wizard_config?.agencyType));
      }
      if (res?.agency_tree?.selected_agencies) {
        dispatch(setMultipleAgency(res?.agency_tree?.selected_agencies));
      }
      if (res.company_groups) {
        dispatch(addAllIndustrysegment(res?.company_groups));
      }
      // dispatch(addAllIndustryLeader(res?.company_groups));
      if (res?.universities) {
        dispatch(addUniversity(res?.universities));
      }
      if (res?.education_fields) {
        dispatch(addEducationFields(res?.education_fields));
      }
      if (res?.company) {
        dispatch(addCandidateWorkCompanyList(res?.company));
      }
      if (res?.locations) {
        dispatch(addLocations(res?.locations));
      }
      if (res?.location_radius && res?.location_radius > 0) {
        dispatch(updateLocationRadius(res.location_radius));
      }
      if (res?.locations_excluded) {
        dispatch(addExcludeLocations(res?.locations_excluded));
      }

      dispatch(addMinExp(res?.min_experience ?? 1));
      dispatch(addMaxExp(res?.max_experience ?? 60));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      const numericJobId = Number(jobId); // Convert to number
      if (!isNaN(numericJobId)) {
        // Ensure it's a valid number
        getSingleProject(numericJobId);
      } else {
        console.error("Invalid jobId:", jobId); // Handle invalid number case
      }
    }
  }, [jobId, activeStep]);

  useEffect(() => {
    if (defaultSelectedProject) {
      if (!isModal && !searchId) {
        //resetKeyword
        dispatch(resetSearchConfig());
        dispatch(resetKeyword());
        if (defaultSelectedProject.keywords) dispatch(addKeyword(defaultSelectedProject.keywords));
        dispatch(resetLocations());
        if (defaultSelectedProject.locations)
          dispatch(addLocation(defaultSelectedProject.locations));
        dispatch(removeAllJobTitle([]));
        if (defaultSelectedProject.title)
          dispatch(addJobTitle(defaultSelectedProject.official_title));

        dispatch(addName(defaultSelectedProject.title));
        if (defaultSelectedProject.min_experience) {
          dispatch(addMinExp(defaultSelectedProject?.min_experience));
        }
        if (defaultSelectedProject.max_experience) {
          dispatch(addMaxExp(defaultSelectedProject?.max_experience));
        }
      } else {
        console.error("Invalid jobId:", defaultSelectedProject.id); // Handle invalid number case
      }
    }
  }, [defaultSelectedProject]);

  useEffect(() => {
    if (searchId) {
      handleChange(Number(searchId));
    }
  }, [searchId]);

  return (
    <Select
      options={options}
      isLoading={loading}
      value={
        defaultSelectedProject
          ? {
              id: defaultSelectedProject?.id?.toString(),
              title: defaultSelectedProject?.title,
            }
          : null
      }
      defaultValue={
        defaultSelectedProject
          ? {
              id: defaultSelectedProject?.id?.toString(),
              title: defaultSelectedProject?.title,
            }
          : null
      }
      placeholder="Select Project..."
      getOptionLabel={(option) => option.title} // Use the title as the label
      getOptionValue={(option) => option.id.toString()} // Use the id as the value
      onInputChange={handleInputChange} // Trigger search on input change
      onChange={(selectedOption: any) => {
        getSingleProject(selectedOption.id);
      }}
      styles={{ ...customStyles, ...styles }} // Apply custom styles if provided
      theme={(theme) => ({
        ...theme,
        borderRadius: 8,
        colors: {
          ...theme.colors,
          primary25: "#F9F5FF",
          primary: "#F9F5FF",
        },
        option: (base: any) => ({
          ...base,
          color: "#101828",
        }),
      })}
      onMenuScrollToBottom={handleMenuScrollToBottom} // Trigger loading more projects on scroll
      className="w-3/5 select-project focus:border-border-theme text-quaternary rounded-lg"
      isDisabled={isModal || searchId ? true : false}
    />
  );
};

export default ProjectSelect;
